import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getNewsById, getRelatedNews, formatNewsDate } from '../../services/newsService';
import './NewsDetailsPage.css';

const NewsDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [newsData, setNewsData] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNewsDetails = async () => {
      if (!id) {
        setError('No article ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch the news article
        const article = await getNewsById(id);
        setNewsData(article);

        // Fetch related articles
        if (article?.category) {
          const related = await getRelatedNews(article.category, article.id, 5);
          setRelatedArticles(related);
        }
      } catch (err) {
        console.error('Error fetching news details:', err);
        setError('Failed to load news article. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetails();
  }, [id]);

  const handleRelatedClick = (articleId) => {
    navigate(`/news/${articleId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="news-details-page">
        <Header />
        <main className="news-details-content">
          <div className="news-loading">
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error || !newsData) {
    return (
      <div className="news-details-page">
        <Header />
        <main className="news-details-content">
          <div className="news-error">
            <h2>Article Not Found</h2>
            <p>{error || 'The requested article could not be found.'}</p>
            <Link to="/news" className="back-to-news-btn">← Back to News</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="news-details-page">
      <Header />
      
      <main className="news-details-content">
        {/* Breadcrumb */}
        <div className="news-breadcrumb">
          <Link to="/" className="news-breadcrumb-link">Home</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Link to="/news" className="news-breadcrumb-link">News</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="news-breadcrumb-current">{newsData.category || 'Details'}</span>
        </div>

        <div className="news-details-wrapper">
          {/* Main Content */}
          <div className="news-main-content">
            {/* Main Section */}
            <div className="news-section">
              {/* News Header */}
              <div className="news-header">
                <span className="news-category-tag">{newsData.category || 'News'}</span>
                <h1 className="news-category">{newsData.title}</h1>
                <div className="news-meta">
                  <span className="news-date">{formatNewsDate(newsData.publishedAt || newsData.createdAt)}</span>
                  {newsData.authorName && <span className="news-author">By {newsData.authorName}</span>}
                  <span className="news-views">{newsData.views || 0} views</span>
                </div>
              </div>

              {/* Main Image */}
              {newsData.image && (
                <img 
                  src={newsData.image} 
                  alt={newsData.title} 
                  className="news-main-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              {/* Excerpt */}
              {newsData.excerpt && (
                <div className="news-excerpt">
                  <p>{newsData.excerpt}</p>
                </div>
              )}

              {/* Content */}
              <div className="news-text-content">
                <p className="news-description">{newsData.content}</p>
              </div>

              {/* Tags */}
              {newsData.tags && (
                <div className="news-tags">
                  {(() => {
                    try {
                      const tags = typeof newsData.tags === 'string' ? JSON.parse(newsData.tags) : newsData.tags;
                      return tags.map((tag, index) => (
                        <span key={index} className="news-tag">{tag}</span>
                      ));
                    } catch {
                      return null;
                    }
                  })()}
                </div>
              )}
            </div>

            {/* Share Section */}
            <div className="news-share-section">
              <span className="share-label">Share this article:</span>
              <div className="share-buttons">
                <button 
                  className="share-btn"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: newsData.title,
                        url: window.location.href
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="18" cy="5" r="3"/>
                    <circle cx="6" cy="12" r="3"/>
                    <circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                  Share
                </button>
              </div>
            </div>

            {/* Back to News */}
            <div className="news-navigation">
              <Link to="/news" className="back-link">← Back to all news</Link>
            </div>
          </div>

          {/* Sidebar - Related Articles */}
          <aside className="news-sidebar">
            <h2 className="news-sidebar-title">Related Articles</h2>
            <div className="news-related-articles">
              {relatedArticles.length > 0 ? (
                relatedArticles.map((article) => (
                  <div 
                    key={article.id} 
                    className="news-related-item"
                    onClick={() => handleRelatedClick(article.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {article.image && (
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="news-related-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="related-article-info">
                      <span className="related-category">{article.category}</span>
                      <h4 className="related-title">{article.title}</h4>
                      <span className="related-date">{formatNewsDate(article.publishedAt || article.createdAt)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-related">No related articles found</p>
              )}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsDetailsPage;
